import { useEffect, useCallback } from 'react';
import GLOBALS from '../config/globals';
import { subPlayer } from '../api/games';
import { getEffectivePitcherStamina } from '../helpers/cardHelpers';
import { isUserPitching } from '../helpers/gameHelper';

const pickReliever = (lineups, pitcherLineupID, managerMode) => {
    const available = lineups
        .filter(gl =>
            gl.Card?.stamina &&
            !gl.gameEventIDExited &&
            !gl.gameEventIDEntered &&
            String(gl.gameLineupID) !== String(pitcherLineupID) &&
            (gl.startingFatigue ?? 0) < gl.Card.stamina
        )
        .sort((a, b) => (b.Card?.rating ?? 0) - (a.Card?.rating ?? 0));

    if (!available.length) return null;
    return managerMode === GLOBALS.AUTO_MANAGER.CRAZY.key
        ? available[Math.floor(Math.random() * available.length)]
        : available[0];
};

const executeAutoSub = async (game, currentPitcher, reliever, refreshGame, stopAuto) => {
    try {
        await subPlayer({
            gameID: game.gameID,
            lineupOrder: currentPitcher.lineupOrder,
            position: currentPitcher.fieldingPosition,
            removedGameLineupID: currentPitcher.gameLineupID,
            addedGameLineupID: reliever.gameLineupID,
        });
        await refreshGame();
    } catch (err) {
        console.error('Auto-sub pitcher failed:', err);
        stopAuto();
    }
};

/**
 * Manages auto-play cycling, auto-roll timer, and auto pitcher substitution.
 *
 * State (autoPlay, managerMode) is owned by the parent — this hook only
 * provides handlers and side effects.
 */
const useAutoManager = ({
    autoPlay,
    setAutoPlay,
    managerMode,
    setManagerMode,
    game,
    participants,
    currentUser,
    rolling,
    isMidEventModalOpen,
    isStealModalOpen,
    isModalOpen,
    isPitcherFatiguedModalOpen,
    handleRollDice,
    refreshGame,
}) => {
    const stopAuto = useCallback(() => {
        setAutoPlay(false);
        setManagerMode(null);
    }, [setAutoPlay, setManagerMode]);

    // ── Auto/manager cycle: off → auto → safe → normal → kev → off ──
    const handleAutoCycle = useCallback(() => {
        if (!autoPlay) {
            setAutoPlay(true);
            setManagerMode(null);
        } else if (!managerMode) {
            setManagerMode(GLOBALS.AUTO_MANAGER.SAFE.key);
        } else if (managerMode === GLOBALS.AUTO_MANAGER.SAFE.key) {
            setManagerMode(GLOBALS.AUTO_MANAGER.NORMAL.key);
        } else if (managerMode === GLOBALS.AUTO_MANAGER.NORMAL.key) {
            setManagerMode(GLOBALS.AUTO_MANAGER.CRAZY.key);
        } else {
            stopAuto();
        }
    }, [autoPlay, managerMode, setAutoPlay, setManagerMode, stopAuto]);

    // ── Auto-roll timer ──
    useEffect(() => {
        if (!autoPlay || game?.isFinal) return;
        if (rolling || isMidEventModalOpen || isStealModalOpen || isModalOpen || isPitcherFatiguedModalOpen) return;

        const timer = setTimeout(handleRollDice, 2000);
        return () => clearTimeout(timer);
    }, [autoPlay, rolling, isMidEventModalOpen, isStealModalOpen, isModalOpen, isPitcherFatiguedModalOpen, game?.isFinal, handleRollDice]);

    // ── Stop on game over ──
    useEffect(() => {
        if (game?.isFinal) stopAuto();
    }, [game?.isFinal, stopAuto]);

    // ── Auto pitcher sub (when manager mode is active) ──
    // Checks pitcher fatigue directly — does not depend on the modal.
    useEffect(() => {
        // 1. Should we check?
        if (!managerMode || !game?.GameEvents?.length) return;
        if (!isUserPitching(game, currentUser)) return;

        // 2. Find the current pitcher
        const userPart = participants?.find(p => String(p.userID) === String(currentUser?.userID));
        const lineups = userPart?.GameLineups ?? [];
        const pitcherLineupID = game.GameEvents[0]?.nextPitcherLineupID;
        const currentPitcher = lineups.find(gl => String(gl.gameLineupID) === String(pitcherLineupID));
        if (!currentPitcher?.Card) return;

        // 3. Is the pitcher fatigued?
        const effStamina = getEffectivePitcherStamina(currentPitcher.Card, currentPitcher.CardInventory?.upgrades);
        if ((game.nextPitcherWork ?? 0) < effStamina) return;

        // 4. Find a reliever
        const reliever = pickReliever(lineups, pitcherLineupID, managerMode);
        if (!reliever) return;

        // 5. Execute the sub
        const timer = setTimeout(() => executeAutoSub(game, currentPitcher, reliever, refreshGame, stopAuto), 500);
        return () => clearTimeout(timer);
    }, [managerMode, participants, currentUser, game, stopAuto, refreshGame]);

    return { handleAutoCycle, stopAuto };
};

export default useAutoManager;
